import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingContext } from 'src/M_Components';
import { getFavoritesConfig } from './index.js';
import { FavoritesContext, useFavorites } from './FavoritesContext';
import { FavoritesCache } from './favoritesCache';

export { FavoritesContext, useFavorites };

export function M_FormFavorites({ entityName, pageUrl, varNameDict, setVarNameDict, onAfterApply, onFavoriteLoad, children }) {
    const { t } = useTranslation();
    const ctx = useContext(LoadingContext);

    const [config, setConfig] = useState(null);
    const [favorites, setFavorites] = useState([]);
    const [registeredFields, setRegisteredFields] = useState({});
    useEffect(() => {
        const cfg = getFavoritesConfig(entityName);
        setConfig(cfg);
    }, [entityName]);

    // Função de registo para os componentes filhos
    const registerField = useCallback((groupName, fieldName, fieldConfig) => {
        setRegisteredFields(prev => {
            const group = prev[groupName] || {};
            // Evita updates desnecessários se já existir e for igual
            if (JSON.stringify(group[fieldName]) === JSON.stringify(fieldConfig)) {
                return prev;
            }
            return {
                ...prev,
                [groupName]: {
                    ...group,
                    [fieldName]: fieldConfig
                }
            };
        });

        // Return unsubscribe function
        return () => {
            setRegisteredFields(prev => {
                const group = prev[groupName] || {};
                const newGroup = { ...group };
                delete newGroup[fieldName];

                // Se o grupo ficar vazio, removemos o grupo também
                if (Object.keys(newGroup).length === 0) {
                    const newPrev = { ...prev };
                    delete newPrev[groupName];
                    return newPrev;
                }

                return {
                    ...prev,
                    [groupName]: newGroup
                };
            });
        };
    }, []);

    // Ler da cache + subscrever as mudancas
    useEffect(() => {
        const cached = FavoritesCache.getByPage(entityName, pageUrl);
        setFavorites(cached);

        const unsubscribe = FavoritesCache.subscribe(() => {
            setFavorites(FavoritesCache.getByPage(entityName, pageUrl));
        });
        return unsubscribe;
    }, [entityName, pageUrl]);

    // Obtém dados do grupo (Merge do JSON + Registos Dinâmicos)
    const getGroupData = useCallback((groupName) => {
        const dynamicFields = registeredFields[groupName] || {};
        const jsonFields = config?.groups?.[groupName]?.fields || {};

        // Merge: Dinâmico tem prioridade sobre JSON
        const allFields = { ...jsonFields, ...dynamicFields }; 

        if (Object.keys(allFields).length === 0) return {};

        const data = {};

        for (const [fieldKey, fieldConfig] of Object.entries(allFields)) {
            const varKey = fieldConfig.varKey || fieldKey;
            const value = varNameDict.get(varKey);

            if (value !== undefined && value !== null && value !== '') {
                const displayValue = fieldConfig.displayKey
                    ? varNameDict.get(fieldConfig.displayKey)
                    : value;
                const idValue = fieldConfig.idKey
                    ? varNameDict.get(fieldConfig.idKey)
                    : null;

                data[fieldKey] = {
                    value,
                    label: fieldConfig.label || fieldKey,
                    displayValue: displayValue || value,
                    idKey: fieldConfig.idKey,
                    idValue: idValue,
                    varKey: varKey
                };
            }
        }

        return data;
    }, [registeredFields, varNameDict, config]);

    const saveFieldFavoriteAuto = async (fieldName, fieldValue, displayValue, groupName, idKey, idValue, varKey, optionData) => {
        try {
            const relatedData = {};
            const vk = varKey || fieldName;
            relatedData[vk] = fieldValue;

            if (idKey && idValue) {
                relatedData[idKey] = idValue;
            }

            if (displayValue && displayValue !== fieldValue) {
                relatedData[`${vk}Display`] = displayValue;
            }

            const metadata = {
                type: 'field',
                fieldName,
                fieldValue,
                displayValue: displayValue || fieldValue,
                groupName: groupName || null,
                idKey: idKey || null,
                idValue: idValue || null,
                varKey: vk,
                relatedData,
                optionData: optionData || null
            };

            const favorite = {
                PageUrl: pageUrl,
                EntityName: entityName,
                DisplayName: fieldName,
                Description: displayValue || fieldValue,
                Category: fieldName,
                Metadata: JSON.stringify(metadata)
            };

            const response = await FavoritesCache.save(favorite);
            if (response && !response.name) {
                ctx.toastMessages('success', '', t('FavoriteSaved'));
            } else {
                ctx.toastMessages('error', '', response?.message || t('ErrorSavingFavorite'));
            }
        } catch (error) {
            console.error('Error saving favorite:', error);
            ctx.toastMessages('error', '', t('ErrorSavingFavorite'));
        }
    };

    // Remove um favorito de campo pelo valor
    const removeFieldFavorite = async (fieldName, fieldValue) => {
        try {
            const fav = favorites.find(f => {
                const meta = typeof f.Metadata === 'string' ? JSON.parse(f.Metadata) : f.Metadata;
                return meta?.type === 'field' && meta?.fieldName === fieldName && meta?.fieldValue === fieldValue;
            });
            if (fav) {
                await FavoritesCache.remove(fav.Id);
                ctx.toastMessages('success', '', t('FavoriteDeleted'));
            }
        } catch (error) {
            console.error('Error removing favorite:', error);
            ctx.toastMessages('error', '', t('ErrorDeletingFavorite'));
        }
    };

    // Retorna array de valores favoritos para um campo
    const getFieldFavoriteValues = useCallback((fieldName) => {
        return favorites
            .filter(f => {
                const meta = typeof f.Metadata === 'string' ? JSON.parse(f.Metadata) : f.Metadata;
                return meta?.type === 'field' && (meta?.fieldName === fieldName || f.Category === fieldName);
            })
            .map(f => {
                const meta = typeof f.Metadata === 'string' ? JSON.parse(f.Metadata) : f.Metadata;
                return meta?.fieldValue;
            })
            .filter(v => v !== undefined && v !== null);
    }, [favorites]);

    // Retorna objetos de opcao reconstruidos a partir dos favoritos guardados
    const getFieldFavoriteData = useCallback((fieldName, labelFields) => {
        return favorites
            .filter(f => {
                const meta = typeof f.Metadata === 'string' ? JSON.parse(f.Metadata) : f.Metadata;
                return meta?.type === 'field' && (meta?.fieldName === fieldName || f.Category === fieldName);
            })
            .map(f => {
                const meta = typeof f.Metadata === 'string' ? JSON.parse(f.Metadata) : f.Metadata;
                if (meta?.optionData) return meta.optionData;
                // Fallback: reconstruct from metadata
                const item = { Id: meta.fieldValue };
                if (labelFields?.length === 1 && meta.displayValue) {
                    item[labelFields[0]] = meta.displayValue;
                }
                return item;
            })
            .filter(item => item?.Id);
    }, [favorites]);

    // Guarda um grupo como favorito (com o nome dado)
    const saveGroupFavorite = async (groupName, name) => {
        if (!name || !name.trim()) {
            ctx.toastMessages('error', '', t('FavoriteNameRequired'));
            return;
        }

        try {
            const groupData = getGroupData(groupName);

            if (Object.keys(groupData).length === 0) {
                ctx.toastMessages('error', '', t('GroupHasNoValues'));
                return;
            }

            // Validar campos obrigatórios do grupo
            const groupFields = registeredFields[groupName] || {};
            const missingRequired = Object.entries(groupFields)
                .filter(([, fieldConfig]) => fieldConfig.required)
                .filter(([fieldName]) => !groupData[fieldName])
                .map(([, fieldConfig]) => fieldConfig.label);

            if (missingRequired.length > 0) {
                ctx.toastMessages('error', '', t('GroupRequiredFieldsMissing') + ': ' + missingRequired.join(', '));
                return;
            }

            const enrichedFields = {};
            Object.entries(groupData).forEach(([fieldName, data]) => {
                const vk = data.varKey || fieldName;
                enrichedFields[fieldName] = {
                    ...data,
                    allRelatedKeys: {}
                };
                if (data.idKey && data.idValue) {
                    enrichedFields[fieldName].allRelatedKeys[data.idKey] = data.idValue;
                }
                enrichedFields[fieldName].allRelatedKeys[vk] = data.value;
            });

            const metadata = {
                type: 'group',
                groupName,
                fields: enrichedFields
            };

            const favorite = {
                PageUrl: pageUrl,
                EntityName: entityName,
                DisplayName: groupName,
                Description: name,
                Category: groupName,
                Metadata: JSON.stringify(metadata)
            };

            const response = await FavoritesCache.save(favorite);
            if (response && !response.name) {
                ctx.toastMessages('success', '', t('FavoriteSaved'));
            } else {
                ctx.toastMessages('error', '', response?.message || t('ErrorSavingFavorite'));
            }
        } catch (error) {
            console.error('Error saving group favorite:', error);
            ctx.toastMessages('error', '', t('ErrorSavingFavorite'));
        }
    };

    // Retorna favoritos filtrados por groupName
    const getGroupFavorites = useCallback((groupName) => {
        return favorites.filter(f => {
            const meta = typeof f.Metadata === 'string' ? JSON.parse(f.Metadata) : f.Metadata;
            return meta?.type === 'group' && (meta?.groupName === groupName || f.Category === groupName);
        });
    }, [favorites]);

    const handleApplyFavorite = (favorite) => {
        try {
            const metadata = typeof favorite.Metadata === 'string'
                ? JSON.parse(favorite.Metadata)
                : favorite.Metadata;

            const loadedData = {};

            if (metadata.type === 'field') {
                if (metadata.relatedData) {
                    Object.entries(metadata.relatedData).forEach(([key, value]) => {
                        varNameDict.set(key, value);
                        loadedData[key] = value;
                    });
                } else {
                    const varKey = metadata.varKey || metadata.fieldName;
                    varNameDict.set(varKey, metadata.fieldValue);
                    loadedData[varKey] = metadata.fieldValue;

                    if (metadata.idKey && metadata.idValue) {
                        varNameDict.set(metadata.idKey, metadata.idValue);
                        loadedData[metadata.idKey] = metadata.idValue;
                    }
                }

            } else if (metadata.type === 'group') {
                // Limpar todos os campos registados deste grupo antes de aplicar o novo favorito
                const groupFields = registeredFields[metadata.groupName] || {};
                const clearedKeys = {};
                Object.entries(groupFields).forEach(([fieldName, fieldConfig]) => {
                    const vk = fieldConfig.varKey || fieldName;
                    if (vk) { varNameDict.set(vk, ''); clearedKeys[vk] = null; }
                    if (fieldConfig.idKey) { varNameDict.set(fieldConfig.idKey, ''); clearedKeys[fieldConfig.idKey] = null; }
                    if (fieldConfig.displayKey) { varNameDict.set(fieldConfig.displayKey, ''); clearedKeys[fieldConfig.displayKey] = null; }
                });
                // loadedData começa com os campos limpos; os valores do favorito irão sobrepor
                Object.assign(loadedData, clearedKeys);

                Object.entries(metadata.fields).forEach(([fieldName, data]) => {
                    if (data.allRelatedKeys) {
                        Object.entries(data.allRelatedKeys).forEach(([key, value]) => {
                            if (value !== null && value !== undefined) {
                                varNameDict.set(key, value);
                                loadedData[key] = value;
                            }
                        });
                    } else {
                        const varKey = data.varKey || fieldName;
                        if (data.value !== null && data.value !== undefined) {
                            varNameDict.set(varKey, data.value);
                            loadedData[varKey] = data.value;

                            if (data.idKey && data.idValue) {
                                varNameDict.set(data.idKey, data.idValue);
                                loadedData[data.idKey] = data.idValue;
                            }
                        }
                    }
                });
            }

            setVarNameDict(new Map(varNameDict));

            if (onFavoriteLoad) {
                onFavoriteLoad(loadedData);
            }

            if (onAfterApply) {
                onAfterApply();
            }

            ctx.toastMessages('success', '', t('FavoriteApplied'));
        } catch (error) {
            console.error('Error applying favorite:', error);
            ctx.toastMessages('error', '', t('ErrorApplyingFavorite'));
        }
    };

    const handleDeleteFavorite = async (favoriteId, event) => {
        event?.stopPropagation();
        try {
            await FavoritesCache.remove(favoriteId);
            ctx.toastMessages('success', '', t('FavoriteDeleted'));
        } catch (error) {
            ctx.toastMessages('error', '', t('ErrorDeletingFavorite'));
        }
    };

    const contextValue = useMemo(() => ({
        favorites,
        saveFieldFavoriteAuto,
        removeFieldFavorite,
        getFieldFavoriteValues,
        getFieldFavoriteData,
        saveGroupFavorite,
        getGroupFavorites,
        handleApplyFavorite,
        handleDeleteFavorite,
        getGroupData,
        registerField
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [favorites, getFieldFavoriteValues, getFieldFavoriteData, getGroupFavorites, getGroupData, registerField]);

    return (
        <FavoritesContext.Provider value={contextValue}>
            {children}
        </FavoritesContext.Provider>
    );
}

export default M_FormFavorites;